import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ComplianceDetail, ReminderRule } from '../api/client'
import { ReminderEditor } from './ReminderEditor'
import './VehicleReminders.css'

function day(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * The Reminders tab on a vehicle.
 *
 * Two lists, and the second one is the point. A vehicle covered by a fleet rule
 * AND carrying its own earns TWO emails, because the ledger keys on the rule —
 * correct, since the two may have different lead times and recipients, but it
 * reads as a duplicate to whoever receives both.
 *
 * So the fleet rules already covering this vehicle are shown FIRST, before the
 * button that adds another. Somebody about to create a thirty-day insurance
 * reminder for one vehicle can see that a fleet rule already does exactly that,
 * at the moment it matters rather than after the second email arrives.
 */
export function VehicleReminders({
  vehicleId,
  registration,
  rules,
  isPending,
  obligations,
}: {
  vehicleId: number
  registration: string
  rules: ReminderRule[]
  isPending: boolean
  obligations: ComplianceDetail[]
}) {
  const [adding, setAdding] = useState<number | null>(null)
  const [editing, setEditing] = useState<number | null>(null)

  const own = rules.filter((r) => r.isOwnRule)
  const inherited = rules.filter((r) => !r.isOwnRule)

  // Obligations with no rule of this vehicle's own, so "add" offers only what
  // can actually be added. Offering a duplicate and then refusing it is worse
  // than not offering it.
  const covered = new Set(own.map((r) => r.complianceTypeId))
  const addable = obligations.filter((o) => !covered.has(o.complianceTypeId))

  if (isPending) return <p className="muted">Loading reminders…</p>

  return (
    <div className="vrem">
      {inherited.length > 0 && (
        <section className="vrem__band vrem__band--inherited">
          <h4>
            Already covered by fleet rules
            <span className="vrem__count">{inherited.length}</span>
          </h4>
          <p className="vrem__note">
            These watch the whole fleet, or this vehicle&rsquo;s site, and cover it without
            anybody naming it. A reminder you add below is <b>additional</b> — this vehicle
            would be warned about twice.
          </p>
          <ul className="vrem__list">
            {inherited.map((r) => (
              <li key={r.reminderRuleId} className="vrem__row vrem__row--muted">
                <div className="vrem__main">
                  <b>{r.name}</b>
                  <span className="vrem__meta">
                    {r.complianceTypeName ?? 'All obligations'} · {r.daysBefore} days before
                    {r.siteName ? ` · ${r.siteName}` : ' · whole fleet'}
                    {!r.isEnabled && ' · paused'}
                  </span>
                </div>
                <Link className="vrem__manage" to="/reminders">
                  Manage
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="vrem__band">
        <h4>
          This vehicle&rsquo;s own reminders
          <span className="vrem__count">{own.length}</span>
        </h4>

        {own.length === 0 && (
          <p className="vrem__note">
            None yet. Add one below, or use the bell beside any obligation on the
            Compliance tab — both open the same form.
          </p>
        )}

        <ul className="vrem__list">
          {own.map((r) => (
            <li key={r.reminderRuleId} className="vrem__row">
              {editing === r.reminderRuleId ? (
                <ReminderEditor
                  vehicleId={vehicleId}
                  registration={registration}
                  complianceTypeId={r.complianceTypeId!}
                  obligationName={r.complianceTypeName ?? 'This obligation'}
                  existing={r}
                  onClose={() => setEditing(null)}
                />
              ) : (
                <>
                  <div className="vrem__main">
                    <b>{r.name}</b>
                    <span className="vrem__meta">
                      {r.complianceTypeName} · {r.daysBefore} days before
                      {r.remindOnDate ? ` · from ${day(r.remindOnDate)}` : ''}
                      {r.sendCalendarInvite ? ' · with invitation' : ''}
                      {!r.isEnabled && ' · paused'}
                    </span>
                    <span className="vrem__to">
                      {r.toAddresses}
                      {r.ccAddresses ? ` · cc ${r.ccAddresses}` : ''}
                    </span>
                    {/* Sent count and last-sent: a rule that has never fired is
                        either new or wrong, and the difference is worth seeing. */}
                    <span className="vrem__meta">
                      {r.sentCount === 0
                        ? 'Never sent'
                        : `Sent ${r.sentCount}×, last ${day(r.lastSentUtc)}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="vrem__manage"
                    onClick={() => setEditing(r.reminderRuleId!)}
                  >
                    Edit
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>

        {adding != null ? (
          <ReminderEditor
            vehicleId={vehicleId}
            registration={registration}
            complianceTypeId={adding}
            obligationName={
              obligations.find((o) => o.complianceTypeId === adding)?.displayName ??
              'This obligation'
            }
            onClose={() => setAdding(null)}
          />
        ) : (
          addable.length > 0 && (
            <div className="vrem__add">
              <span>Add a reminder for</span>
              {/*
                One button per obligation rather than a picker plus a submit. The
                whole gesture is "warn me about fitness", and a two-step form for
                a one-word decision is the kind of friction that leaves reminders
                unset.
              */}
              {addable.map((o) => (
                <button
                  key={o.complianceTypeId}
                  type="button"
                  className="vrem__addbtn"
                  onClick={() => setAdding(o.complianceTypeId!)}
                >
                  {o.displayName}
                </button>
              ))}
            </div>
          )
        )}
      </section>
    </div>
  )
}
